import React, { useState, useEffect } from 'react'
import { DndContext, closestCenter } from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'

const { FiPlus, FiMoreVertical, FiCopy, FiTrash2, FiEdit3 } = FiIcons

const SlidePanel = ({ slides, activeSlideIndex, onSlideSelect, onAddSlide, onDeleteSlide, onDuplicateSlide, onReorderSlides, onUpdateSlideTitle }) => {
  const [slidePreviews, setSlidePreviews] = useState({})

  // Update slide previews when slides change
  useEffect(() => {
    const newPreviews = {}
    slides.forEach((slide, index) => {
      newPreviews[slide.id] = generateSlidePreview(slide, index)
    })
    setSlidePreviews(newPreviews)
  }, [slides])

  const generateSlidePreview = (slide, index) => {
    const titleElement = slide.elements.find(el => 
      el.type === 'text' && (parseInt(el.style.fontSize) >= 36 || el.style.fontWeight === 'bold')
    )
    const textElements = slide.elements.filter(el => el.type === 'text')
    const imageElements = slide.elements.filter(el => el.type === 'image')
    const shapeElements = slide.elements.filter(el => el.type === 'shape')
    
    if (titleElement) {
      return {
        title: titleElement.content,
        subtitle: `${slide.elements.length} element${slide.elements.length !== 1 ? 's' : ''}`,
        elements: slide.elements
      }
    }
    
    if (textElements.length > 0) {
      // Handle different content formats
      let contentText = ''
      const content = textElements[0].content
      
      if (typeof content === 'string') {
        contentText = content
      } else if (Array.isArray(content)) {
        contentText = content.join(', ')
      } else if (content && typeof content === 'object') {
        // Handle object content (shouldn't happen after transformation but just in case)
        contentText = content.text || content.items?.join(', ') || '[Complex content]'
      }
      
      const previewText = contentText.slice(0, 30) + (contentText.length > 30 ? '...' : '')
      
      return {
        title: previewText || `Slide ${index + 1}`,
        subtitle: `${slide.elements.length} element${slide.elements.length !== 1 ? 's' : ''}`,
        elements: slide.elements
      }
    }

    return {
      title: `Slide ${index + 1}`,
      subtitle: `${slide.elements.length} element${slide.elements.length !== 1 ? 's' : ''}`,
      elements: slide.elements
    }
  }

  const handleDragEnd = (event) => {
    const { active, over } = event
    
    if (active.id !== over.id) {
      const oldIndex = slides.findIndex(slide => slide.id === active.id)
      const newIndex = slides.findIndex(slide => slide.id === over.id)
      
      onReorderSlides(oldIndex, newIndex)
    }
  }

  return (
    <div className="h-full flex flex-col">
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-medium text-gray-900">Slides</h3>
          <button
            onClick={() => onAddSlide()}
            className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <SafeIcon icon={FiPlus} className="w-4 h-4 text-gray-600" />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-2">
        <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={slides.map(slide => slide.id)} strategy={verticalListSortingStrategy}>
            <div className="space-y-2">
              {slides.map((slide, index) => (
                <SortableSlideThumb
                  key={slide.id}
                  slide={slide}
                  index={index}
                  isActive={index === activeSlideIndex}
                  onClick={() => onSlideSelect(index)}
                  onDuplicate={() => onDuplicateSlide(index)}
                  onDelete={() => onDeleteSlide(index)}
                  onUpdateTitle={(newTitle) => onUpdateSlideTitle(index, newTitle)}
                  canDelete={slides.length > 1}
                  preview={slidePreviews[slide.id]}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      </div>
    </div>
  )
}

const SortableSlideThumb = ({ slide, index, isActive, onClick, onDuplicate, onDelete, onUpdateTitle, canDelete, preview }) => {
  const [menuOpen, setMenuOpen] = useState(false)
  const [isEditingTitle, setIsEditingTitle] = useState(false)
  const [titleValue, setTitleValue] = useState(slide.title || `Slide ${index + 1}`)

  // Update title value when slide prop changes
  React.useEffect(() => {
    setTitleValue(slide.title || `Slide ${index + 1}`)
  }, [slide.title, index])
  
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: slide.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  const getBackgroundStyle = () => {
    if (slide.background?.type === 'gradient') {
      return slide.background.value
    } else if (slide.background?.type === 'image') {
      return `url(${slide.background.url}) center/cover`
    } else {
      return slide.background?.value || '#ffffff'
    }
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`relative group p-3 rounded-lg border-2 cursor-pointer transition-all ${
        isActive
          ? 'border-primary-500 bg-primary-50'
          : 'border-gray-200 hover:border-gray-300 bg-white'
      }`}
      onClick={onClick}
    >
      {/* Drag handle */}
      <div
        {...attributes}
        {...listeners}
        className="absolute left-1 top-1/2 transform -translate-y-1/2 opacity-0 group-hover:opacity-100 cursor-grab active:cursor-grabbing"
      >
        <div className="w-2 h-8 flex flex-col justify-center space-y-0.5">
          <div className="w-full h-0.5 bg-gray-400 rounded"></div>
          <div className="w-full h-0.5 bg-gray-400 rounded"></div>
          <div className="w-full h-0.5 bg-gray-400 rounded"></div>
        </div>
      </div>

      {/* Slide Preview */}
      <div 
        className="aspect-video bg-gradient-to-br from-gray-50 to-gray-100 rounded border mb-2 relative overflow-hidden ml-4 slide-thumbnail"
        data-slide-id={slide.id}
        data-slide-index={index}
        style={{ background: getBackgroundStyle() }}
      >
        {/* Mini elements preview */}
        {preview?.elements?.slice(0, 4).map((element, i) => (
          <div
            key={element.id}
            className={`absolute text-xs overflow-hidden ${
              element.type === 'text' ? 'text-gray-700' : 
              element.type === 'image' ? 'bg-gray-300' :
              element.type === 'shape' ? '' : 'bg-gray-300'
            }`}
            style={{
              left: `${Math.max(0, Math.min(85, (element.style.position.x / 800) * 100))}%`,
              top: `${Math.max(0, Math.min(85, (element.style.position.y / 600) * 100))}%`,
              width: `${Math.min(20, (element.style.width / 800) * 100)}%`,
              height: element.type === 'image' || element.type === 'shape' 
                ? `${Math.min(15, (element.style.height / 600) * 100)}%` 
                : 'auto',
              fontSize: '6px',
              lineHeight: '1.2',
              backgroundColor: element.type === 'shape' ? element.style.backgroundColor : undefined,
              borderRadius: element.type === 'shape' ? element.style.borderRadius : undefined
            }}
          >
            {element.type === 'text' && element.content?.slice(0, 15)}
            {element.type === 'list' && '• List'}
            {element.type === 'image' && ''}
            {element.type === 'shape' && ''}
          </div>
        ))}
      </div>

      {/* Slide Info */}
      <div className="flex items-center justify-between ml-4">
        <div className="flex-1 mr-2">
          {isEditingTitle ? (
            <input
              type="text"
              value={titleValue}
              onChange={(e) => setTitleValue(e.target.value)}
              onBlur={() => {
                setIsEditingTitle(false)
                if (titleValue.trim() && titleValue !== slide.title) {
                  onUpdateTitle(titleValue.trim())
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  setIsEditingTitle(false)
                  if (titleValue.trim() && titleValue !== slide.title) {
                    onUpdateTitle(titleValue.trim())
                  }
                } else if (e.key === 'Escape') {
                  setTitleValue(slide.title || `Slide ${index + 1}`)
                  setIsEditingTitle(false)
                }
              }}
              onClick={(e) => e.stopPropagation()}
              className="text-xs font-medium text-gray-900 bg-white border border-primary-300 rounded px-1 py-0.5 w-full focus:outline-none focus:ring-1 focus:ring-primary-500"
              autoFocus
            />
          ) : (
            <p 
              className="text-xs font-medium text-gray-900 truncate cursor-pointer hover:text-primary-600"
              onClick={(e) => {
                e.stopPropagation()
                setIsEditingTitle(true)
              }}
            >
              {slide.title || `Slide ${index + 1}`}
            </p>
          )}
          <p className="text-xs text-gray-500">{preview?.subtitle || 'Empty slide'}</p>
        </div>

        {/* Actions Menu */}
        <div className="relative">
          <button 
            className="opacity-0 group-hover:opacity-100 p-1 hover:bg-gray-200 rounded transition-all"
            onClick={(e) => {
              e.stopPropagation()
              setMenuOpen(!menuOpen)
            }}
          >
            <SafeIcon icon={FiMoreVertical} className="w-3 h-3 text-gray-400" />
          </button>
          
          {/* Dropdown Menu */}
          {menuOpen && (
            <>
              <div 
                className="fixed inset-0 z-10" 
                onClick={() => setMenuOpen(false)}
              ></div>
              <div className="absolute right-0 top-8 bg-white border border-gray-200 rounded-lg shadow-xl py-1 z-20 min-w-[120px]">
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    onDuplicate()
                    setMenuOpen(false)
                  }}
                  className="flex items-center space-x-2 px-3 py-2 text-xs text-gray-700 hover:bg-gray-50 w-full text-left"
                >
                  <SafeIcon icon={FiCopy} className="w-3 h-3" />
                  <span>Duplicate</span>
                </button>
                {canDelete && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      onDelete()
                      setMenuOpen(false)
                    }}
                    className="flex items-center space-x-2 px-3 py-2 text-xs text-red-600 hover:bg-red-50 w-full text-left"
                  >
                    <SafeIcon icon={FiTrash2} className="w-3 h-3" />
                    <span>Delete</span>
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default SlidePanel